/**
 * HTTP-based RemoteCommands for code search against the Morph landing API.
 *
 * Translates grep/read/listDir into POST requests to the code-search endpoints,
 * so the WarpGrep agent can search GitHub repositories that have been indexed
 * by the Morph code storage service.
 */

import type { RemoteCommands } from '../types.js';

/**
 * The WarpGrep model addresses files as if the repo were checked out at
 * /home/ubuntu/repo (its training root), while the code-search endpoints
 * expect repo-relative paths. Strip the training root and any leading
 * slashes at this boundary — the same normalization the dashboard
 * playground applies via warpgrep-mock-paths.ts.
 */
const MODEL_REPO_ROOT = /^\/?home\/ubuntu\/repo(?:\/|$)/;

export function toRepoRelative(path: string): string {
  if (!path) return path;
  const stripped = path.replace(MODEL_REPO_ROOT, '').replace(/^\.?\/+/, '');
  return stripped || '.';
}

export interface CodeStorageHttpConfig {
  /** Base URL for the code search API (e.g. "https://api.morphllm.com") */
  baseUrl: string;
  /** Repository ID returned by the get-or-create endpoint */
  repoId: string;
  /** Git branch to search */
  branch: string;
  /** Morph API key, forwarded as a Bearer token (api.morphllm.com requires it) */
  apiKey?: string;
}

async function post(url: string, body: Record<string, unknown>, op: string, apiKey?: string): Promise<string> {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(apiKey ? { Authorization: `Bearer ${apiKey}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    // Return error as content instead of throwing so the agent can handle it gracefully
    try {
      const parsed = JSON.parse(text);
      if (parsed.error) return `Error: ${parsed.error}`;
    } catch { /* not JSON, fall through */ }
    return `Error: ${op} failed (${res.status}): ${text}`;
  }
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data.stdout;
}

export function createCodeStorageHttpCommands(config: CodeStorageHttpConfig): RemoteCommands {
  const { baseUrl, repoId, branch, apiKey } = config;
  const encodedRepoId = encodeURIComponent(repoId);

  return {
    grep: (pattern: string, path: string, glob?: string) =>
      post(`${baseUrl}/api/code-search/${encodedRepoId}/grep`, { pattern, path: toRepoRelative(path), glob, branch }, 'grep', apiKey),

    read: (path: string, start: number, end: number) =>
      post(`${baseUrl}/api/code-search/${encodedRepoId}/read`, { path: toRepoRelative(path), start, end, branch }, 'read', apiKey),

    listDir: (path: string, maxDepth: number) =>
      post(`${baseUrl}/api/code-search/${encodedRepoId}/list`, { path: toRepoRelative(path), maxDepth, branch }, 'list', apiKey),
  };
}
